import { z } from 'zod';
import { format, startOfDay } from 'date-fns';
import { normaliseMileage } from '@/features/vehiclesearch/utils/mileage';
import { looksLikeCardNumber } from '../utils/looksLikeCardNumber';
import { mileageBelowRecordedWarning } from '../utils/mileageWarning';
import type { FundingOverridePayload, InvoiceLinePayload, SubmitInvoicePayload } from '../types/agreements.types';

const MAX_MILEAGE = 2_000_000;

export const MILEAGE_CONFIRMATION_MESSAGE = 'Confirm the mileage is correct';

function mileageIssue(raw: string): string | null {
    const digits = normaliseMileage(raw);
    if (digits === '') return 'Enter the mileage';
    if (!/^\d+$/.test(digits)) return 'Enter the mileage as a whole number';
    const miles = Number(digits);
    if (miles > MAX_MILEAGE) return "Mileage can't be more than 2,000,000 miles";
    return null;
}

export const invoiceNumberField = z
    .string()
    .trim()
    .min(1, 'Enter the invoice number')
    .max(50, 'Invoice number must be 50 characters or fewer')
    .refine((value) => !looksLikeCardNumber(value), 'Invoice number must not contain a card number');

export const mileageField = z.string().superRefine((raw, ctx) => {
    const message = mileageIssue(raw);
    if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
});

export const invoiceSubmitSchema = (recordedMileage: number | null) =>
    z.object({
        invoiceNumber: invoiceNumberField,
        invoicedAt: z
            .date({ required_error: 'Enter the invoiced date', invalid_type_error: 'Enter the invoiced date' })
            .refine((date) => startOfDay(date) <= startOfDay(new Date()), 'Invoiced date cannot be after today'),
        invoicingDealerId: z.string().uuid('Choose the invoicing dealer'),
        mileage: mileageField,
        mileageConfirmed: z.boolean().optional(),
    }).superRefine((values, ctx) => {
        const needsConfirmation =
            mileageIssue(values.mileage) === null && mileageBelowRecordedWarning(values.mileage, recordedMileage) !== null;
        if (needsConfirmation && !values.mileageConfirmed) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['mileageConfirmed'],
                message: MILEAGE_CONFIRMATION_MESSAGE,
            });
        }
    });

export type InvoiceSubmitValues = z.infer<ReturnType<typeof invoiceSubmitSchema>>;

/** Local calendar date, never `toISOString()` — that shifts to UTC and can
 *  render the wrong day for a viewer west of Greenwich. */
export function toSubmitPayload(
    values: InvoiceSubmitValues,
    lines: InvoiceLinePayload[],
    override?: FundingOverridePayload,
): SubmitInvoicePayload {
    return {
        invoice_number: values.invoiceNumber,
        invoiced_at: format(values.invoicedAt, 'yyyy-MM-dd'),
        invoicing_dealer_id: values.invoicingDealerId,
        mileage: Number(normaliseMileage(values.mileage)),
        lines,
        ...(override ? { funding_override: override } : {}),
    };
}
